import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { TabsModule } from 'primeng/tabs';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import { UserService } from '../services/user/user.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom } from 'rxjs';

interface FieldConfig {
  id: number;
  template_id: number;
  field_name: string;
  display_name: string;
  field_type: string;
  data_type: string;
  is_mandatory: boolean;
  is_visible: boolean;
  is_custom_field: boolean;
  tab_id: string | number;
  sub_tab_id: number;
  display_order: number;
  validation_rules: string;
  help_text: string;
  default_value: any;
  is_active: boolean;
  is_deleted: boolean;
  width: number;
  options?: any[];
}

interface SubTabConfig {
  id: number;
  title: string;
  sub_type: 'table' | 'field';
  show_heading: boolean;
  is_active: boolean;
  is_deleted: boolean;
  type?: string;
  content: FieldConfig[];
}

interface TabConfig {
  id: number;
  name: string;
  is_active: boolean;
  is_deleted: boolean;
  sub_tabs: SubTabConfig[];
}

interface DynamicFormTab {
  title: string;
  value: number;
  sub_tabs: SubTabConfig[];
}

@Component({
  selector: 'lib-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputTextModule,
    SelectModule,
    ButtonModule,
    AccordionModule,
    TabsModule,
    DynamicForm
  ],
  templateUrl: './user.html',
  styleUrls: ['./user.css']
})
export class User implements OnInit {
  userId: string | null = null;
  mode: string = 'create';
  isLoading = false;
  isSubmitting = false;
  userForm!: FormGroup;
  
  data: FieldConfig[] = [];
  primaryFields: FieldConfig[] = [];
  response: any;
  selectedTemplate: any = null;
  loadedTemplateId: string | null = null;
  
  tabData: TabConfig[] = [];
  dynamicFormTabs: DynamicFormTab[] = [];
  activeTabIndex = 2; // Start with tab 2 (Roles) since tab 1 is in accordion
  initializedTabs: Set<number> = new Set();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private userService: UserService,
    private subsidiaryService: SubsidiaryService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.isLoading = true;
    
    // Get mode and userId from route params
    const routeParams = this.route.snapshot.params;
    const currentUrl = this.router.url;
    
    if (routeParams['userId']) {
      this.userId = routeParams['userId'];
      // Determine mode from URL path
      if (currentUrl.includes('/edit/')) {
        this.mode = 'edit';
      } else if (currentUrl.includes('/view/')) {
        this.mode = 'view';
      } else {
        this.mode = 'view'; // default to view if userId is present
      }
    } else {
      this.mode = 'create';
      this.userId = null;
    }

    await this.loadAllData();
    this.isLoading = false;
  }

  async loadAllData() {
    try {
      // Create basic form structure
      this.createBasicFormStructure();
      this.processTemplateData();
      
      // Load subsidiary options
      await this.loadSubsidiaries();
      
      // Load user data if editing/viewing
      if (this.userId && (this.mode === 'edit' || this.mode === 'view')) {
        await this.loadUserData();
      } else {
        this.initializeForm();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      this.toasterService.error('Error', 'Failed to load form data');
    }
  }
  
  private async loadSubsidiaries(): Promise<void> {
    try {
      const response = await lastValueFrom(
        this.subsidiaryService.getSubsidiaries({ include_inactive: 'false' })
      );
      const subsidiaries = response?.data?.subsidiaries || response?.data || [];
      
      const subsidiaryOptions = Array.isArray(subsidiaries)
        ? subsidiaries.map((subsidiary: any) => ({
            id: subsidiary.id,
            name: subsidiary.name || subsidiary.subsidiary_name || '',
            value: subsidiary.id,
            label: subsidiary.name || subsidiary.subsidiary_name || '',
            ...subsidiary
          }))
        : [];
      
      // Update subsidiary_id field options in primaryFields
      const subsidiaryField = this.primaryFields.find(f => f.field_name === 'subsidiary_id');
      if (subsidiaryField) {
        subsidiaryField.options = subsidiaryOptions;
      }
      
      // Also update in data array
      const subsidiaryFields = this.data.filter(f => f.field_name === 'subsidiary_id');
      subsidiaryFields.forEach(field => {
        field.options = subsidiaryOptions;
      });
    } catch (error) {
      console.error('Error loading subsidiaries:', error);
      // Continue without subsidiaries if API call fails
    }
  }

  createBasicFormStructure() {
    // Hardcoded tabData structure with tabs, subtabs, and fields
    this.tabData = [
      {
        id: 1,
        name: 'Primary Information',
        is_active: true,
        is_deleted: false,
        sub_tabs: [
          {
            id: 1,
            title: 'User Information',
            sub_type: 'field',
            show_heading: false,
            is_active: true,
            is_deleted: false,
            content: [
              {
                id: 1,
                template_id: 0,
                field_name: 'first_name',
                display_name: 'First Name',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 1,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 2,
                template_id: 0,
                field_name: 'last_name',
                display_name: 'Last Name',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 2,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 3,
                template_id: 0,
                field_name: 'email',
                display_name: 'Email',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: true,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 3,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 4,
                template_id: 0,
                field_name: 'phone_number',
                display_name: 'Phone',
                field_type: 'input',
                data_type: 'string',
                is_mandatory: false,
                is_visible: true,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 4,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1
              },
              {
                id: 5,
                template_id: 0,
                field_name: 'subsidiary_id',
                display_name: 'Subsidiary',
                field_type: 'select',
                data_type: 'string',
                is_mandatory: false,
                is_visible: false,
                is_custom_field: false,
                tab_id: 1,
                sub_tab_id: 1,
                display_order: 5,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 2,
                options: [
                ]
              }
            ]
          }
        ]
      },
      {
        id: 2,
        name: 'Roles',
        is_active: true,
        is_deleted: false,
        sub_tabs: [
          {
            id: 1,
            title: 'Role Information',
            sub_type: 'field',
            show_heading: false,
            is_active: true,
            is_deleted: false,
            content: [
              {
                id: 4,
                template_id: 0,
                field_name: 'role',
                display_name: 'Role',
                field_type: 'select',
                data_type: 'string',
                is_mandatory: false,
                is_visible: true,
                is_custom_field: false,
                tab_id: 2,
                sub_tab_id: 1,
                display_order: 1,
                validation_rules: '',
                help_text: '',
                default_value: '',
                is_active: true,
                is_deleted: false,
                width: 1,
                options: [
                  { label: 'Admin', value: 'admin' },
                  { label: 'User', value: 'user' },
                  { label: 'Manager', value: 'manager' }
                ]
              },
              // {
              //   id: 5,
              //   template_id: 0,
              //   field_name: 'department',
              //   display_name: 'Department',
              //   field_type: 'select',
              //   data_type: 'string',
              //   is_mandatory: false,
              //   is_visible: true,
              //   is_custom_field: false,
              //   tab_id: 2,
              //   sub_tab_id: 1,
              //   display_order: 2,
              //   validation_rules: '',
              //   help_text: '',
              //   default_value: '',
              //   is_active: true,
              //   is_deleted: false,
              //   width: 1,
              //   options: [
              //     { label: 'IT', value: 'it' },
              //     { label: 'HR', value: 'hr' },
              //     { label: 'Finance', value: 'finance' },
              //     { label: 'Operations', value: 'operations' }
              //   ]
              // }
            ]
          }
        ]
      }
    ];
    
    // Extract all fields from tabData for backward compatibility
    this.data = [];
    this.tabData.forEach(tab => {
      tab.sub_tabs.forEach(subTab => {
        this.data.push(...subTab.content);
      });
    });
    
    // Build dynamicFormTabs from tabData
    this.buildDynamicFormTabs();
  }
  
  
  private buildDynamicFormTabs(): void {
    // Filter out tab 1 (Primary Information) - it will be shown in accordion
    // Only include tabs with id > 1 (like Roles)
    const tabsToShow = this.tabData.filter((tab: TabConfig) => tab.id > 1);
    
    this.dynamicFormTabs = tabsToShow.map((tab: TabConfig) => ({
      title: this.capitalize(tab.name),
      value: tab.id,
      sub_tabs: (tab.sub_tabs ?? []).map((subTab: SubTabConfig) => ({
        ...subTab,
        title: subTab.title,
        sub_type: subTab.sub_type ?? 'field',
        type: subTab.type ?? subTab.sub_type,
        content: this.data.filter(f => f.tab_id == tab.id && f.sub_tab_id == subTab.id)
      }))
    }));
    
    if (this.dynamicFormTabs.length > 0) {
      const firstTabValue = this.dynamicFormTabs[0]?.value;
      if (!this.initializedTabs.has(this.activeTabIndex) ||
          !this.dynamicFormTabs.find(t => t.value === this.activeTabIndex)) {
        this.activeTabIndex = Number(firstTabValue);
      }
      
      // Don't initialize tab data here - wait for form to be created
      // initializeTabData will be called from initializeForm()
    }
  }
  
  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  }
  
  private initializeTabData(tabValue: number): void {
    if (this.initializedTabs.has(tabValue)) {
      return;
    }
    
    // Ensure userForm exists before trying to use it
    if (!this.userForm) {
      return;
    }
    
    const tab = this.dynamicFormTabs.find(t => t.value == tabValue);
    if (!tab) {
      return;
    }
    
    // Initialize form controls for this tab
    tab.sub_tabs.forEach(subTab => {
      subTab.content.forEach(field => {
        const fieldKey = this.getFieldKey(field.field_name);
        if (!this.userForm.get(fieldKey)) {
          // For multiselect fields, default_value should be an array
          // For select fields, default_value should be a string
          const value = field.field_type === 'multiselect' 
            ? (field.default_value || [])
            : (field.default_value || '');
          const validators = field.is_mandatory ? [Validators.required] : [];
          this.userForm.addControl(fieldKey, this.fb.control(value, validators));
        }
      });
    });
    
    this.initializedTabs.add(tabValue);
  }
  
  onTabChange(tabValue: any): void {
    this.activeTabIndex = tabValue;
    if (!this.initializedTabs.has(tabValue)) {
      this.initializeTabData(tabValue);
    }
  }

  processTemplateData() {
    // For now, we're using hardcoded tabData structure
    // This method can be used later if we need to load from API
    if (this.response?.data) {
      // If template data is provided, we can merge it with hardcoded structure
      // For now, just use the hardcoded structure
    }
    
    // Filter primary fields (tab_id = 1) for backward compatibility
    this.primaryFields = this.data
      .filter((field: FieldConfig) => field.tab_id === 1 && field.is_visible && !field.is_deleted)
      .sort((a: FieldConfig, b: FieldConfig) => a.display_order - b.display_order);
  }

  initializeForm(userData?: any) {
    const formControls: any = {};
    
    // Split username into first_name and last_name if editing
    if (userData?.username) {
      const nameParts = userData.username.trim().split(/\s+/);
      userData.first_name = nameParts[0] || '';
      userData.last_name = nameParts.slice(1).join(' ') || '';
    }
    
    // Initialize form controls from primary fields (tab 1)
    this.primaryFields.forEach((field: FieldConfig) => {
      const fieldKey = this.getFieldKey(field.field_name);
      const value = userData?.[field.field_name] || field.default_value || '';
      const validators = field.is_mandatory ? [Validators.required] : [];
      
      formControls[fieldKey] = [value, validators];
    });
    
    // Initialize form controls from all tabs (excluding tab 1 which is in primaryFields)
    this.dynamicFormTabs.forEach(tab => {
      tab.sub_tabs.forEach(subTab => {
        subTab.content.forEach((field: FieldConfig) => {
          const fieldKey = this.getFieldKey(field.field_name);
          // For multiselect fields, default_value should be an array
          // For select fields, default_value should be a string
          const defaultValue = field.field_type === 'multiselect' 
            ? (field.default_value || [])
            : (field.default_value || '');
          const value = userData?.[field.field_name] || defaultValue;
          const validators = field.is_mandatory ? [Validators.required] : [];
          
          formControls[fieldKey] = [value, validators];
        });
      });
    });
    
    this.userForm = this.fb.group(formControls);
    
    // Initialize first tab if tabs exist
    if (this.dynamicFormTabs.length > 0) {
      // Ensure activeTabIndex is valid
      if (!this.dynamicFormTabs.find(t => t.value === this.activeTabIndex)) {
        this.activeTabIndex = this.dynamicFormTabs[0]?.value || 2;
      }
      this.initializeTabData(this.activeTabIndex);
    }
    
    if (this.mode === 'view') {
      this.userForm.disable();
    }
  }

  async loadUserData() {
    if (!this.userId) return;
    
    try {
      const response = await lastValueFrom(this.userService.getUserById(this.userId));
      const userData = response?.data || response;
      if (userData) {
        // Extract role from centers array (first center's role)
        if (userData.centers && userData.centers.length > 0) {
          userData.role = userData.centers[0].role;
        }
        this.initializeForm(userData);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      this.toasterService.error('Error', 'Failed to load user data');
    }
  }

  getFieldKey(fieldName: string): string {
    return fieldName;
  }

  onFormFieldChanged(event: { key: string; value: any }) {
    // Handle field changes if needed
  }

  async onSubmit() {
    if (this.userForm.invalid) {
      this.toasterService.error('Error', 'Please fill all required fields');
      return;
    }

    this.isSubmitting = true;
    
    try {
      const formValue = this.userForm.value;
      const payload: any = {};
      
      // Merge first_name and last_name into username
      const firstName = formValue['first_name'] || '';
      const lastName = formValue['last_name'] || '';
      payload.username = `${firstName} ${lastName}`.trim();
      
      // Map form values back to API format from primary fields
      this.primaryFields.forEach((field: FieldConfig) => {
        const fieldKey = this.getFieldKey(field.field_name);
        const value = formValue[fieldKey];
        
        // Skip first_name and last_name as they're merged into username
        if (field.field_name === 'first_name' || field.field_name === 'last_name') {
          return;
        }
        
        // Handle subsidiary_id separately - it will be sent as selected value
        if (field.field_name === 'subsidiary_id') {
          payload.subsidiary_id = value;
        } else {
          payload[field.field_name] = value;
        }
      });
      
      // Always send center_id as 1 in the payload
      payload.center_id = 1;
      
      // Map form values back to API format from all tabs (excluding tab 1)
      this.dynamicFormTabs.forEach(tab => {
        tab.sub_tabs.forEach(subTab => {
          subTab.content.forEach((field: FieldConfig) => {
            const fieldKey = this.getFieldKey(field.field_name);
            payload[field.field_name] = formValue[fieldKey];
          });
        });
      });
      
      if (this.mode === 'create') {
        await lastValueFrom(this.userService.createUser(payload));
        this.toasterService.success('Success', 'User created successfully');
        this.router.navigate(['/settings/account_settings/users']);
      } else if (this.mode === 'edit' && this.userId) {
        // Include ID in the payload body for update (convert to number)
        payload.id = Number(this.userId);
        await lastValueFrom(this.userService.updateUser(payload));
        this.toasterService.success('Success', 'User updated successfully');
        this.router.navigate(['/settings/account_settings/users']);
      }
    } catch (error: any) {
      console.error('Error saving user:', error);
      this.toasterService.error('Error', error?.error?.message || 'Failed to save user');
    } finally {
      this.isSubmitting = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/account_settings/users']);
  }
}

